import { Twitter, Facebook, MessageCircle, Send, Link2, Check } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/hooks/use-toast';

interface ShareButtonsProps {
  title: string;
  text: string;
  url?: string;
  hashtags?: string[];
  className?: string;
}

export const ShareButtons = ({ 
  title, 
  text, 
  url = window.location.href,
  hashtags = ['SuperSociety', 'JeuxIA'],
  className = ''
}: ShareButtonsProps) => {
  const [copied, setCopied] = useState(false);

  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(url);
  const encodedHashtags = hashtags.join(',');

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}&hashtags=${encodedHashtags}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
    whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      setCopied(true);
      toast({
        title: 'Lien copié !',
        description: 'Le lien a été copié dans le presse-papier.',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: 'Erreur',
        description: 'Impossible de copier le lien.',
        variant: 'destructive',
      });
    }
  };

  const handleShare = (platform: keyof typeof shareLinks) => {
    window.open(shareLinks[platform], '_blank', 'width=600,height=400');
  };

  const buttonClass = "w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110";

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Twitter/X */}
      <button
        onClick={() => handleShare('twitter')}
        className={`${buttonClass} bg-black hover:bg-gray-800 text-white`}
        aria-label="Partager sur Twitter"
      >
        <Twitter className="w-4 h-4" />
      </button>

      {/* Facebook */}
      <button
        onClick={() => handleShare('facebook')}
        className={`${buttonClass} bg-[#1877F2] hover:bg-[#166FE5] text-white`}
        aria-label="Partager sur Facebook"
      >
        <Facebook className="w-4 h-4" />
      </button>

      {/* WhatsApp */}
      <button
        onClick={() => handleShare('whatsapp')}
        className={`${buttonClass} bg-[#25D366] hover:bg-[#20BD5A] text-white`}
        aria-label="Partager sur WhatsApp"
      >
        <MessageCircle className="w-4 h-4" />
      </button>

      {/* Telegram */}
      <button
        onClick={() => handleShare('telegram')}
        className={`${buttonClass} bg-[#0088CC] hover:bg-[#007AB8] text-white`}
        aria-label="Partager sur Telegram"
      >
        <Send className="w-4 h-4" />
      </button>

      {/* Copy Link */}
      <button
        onClick={copyToClipboard}
        className={`${buttonClass} bg-card border border-border hover:border-accent text-foreground`}
        aria-label="Copier le lien"
      >
        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Link2 className="w-4 h-4" />}
      </button>
    </div>
  );
};

export const ShareResult = ({ 
  gameName, 
  result, 
  score 
}: { 
  gameName: string; 
  result?: 'win' | 'loss' | 'draw';
  score?: number;
}) => {
  const resultText = result === 'win' 
    ? '🏆 J\'ai gagné' 
    : result === 'loss' 
    ? 'J\'ai joué' 
    : 'J\'ai participé';

  const text = score 
    ? `${resultText} à ${gameName} avec ${score} points sur SuperSociety.ai !`
    : `${resultText} à ${gameName} sur SuperSociety.ai !`;

  return (
    <div className="p-4 rounded-xl bg-card/50 border border-border/50 space-y-3">
      <p className="text-sm text-muted-foreground">Partagez votre résultat</p>
      <ShareButtons 
        title={`${gameName} - SuperSociety.ai`}
        text={text}
        url={window.location.origin}
      />
    </div>
  );
};
